import React, { useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { CircularProgress, ThemeProvider, Box } from '@material-ui/core';

import { AccountIntent, useActivation } from '../ActivationContext';
import { useActivationBase } from '../hooks/useActivationBase';

import { getSupabaseClient } from '@/utils/supabaseClient';
import { activateSleepioConfirm } from '@/utils/sleepioActivationApi';
import { logError } from '@/utils/logError';
import { buildRedirectParam } from '@/utils/urlHelpers';
import { REDIRECT_TO_SESSION_KEY } from '@/utils/constants';
import sleepioTheme from '@/themes/sleepioTheme';

// Hardcoded fallback so ASWebAuthenticationSession can intercept the exchange token
// redirect when sessionStorage is empty (ephemeral session opened by the Canopy
// foreground fallback resolver).
const CANOPY_NATIVE_CALLBACK = 'sleep-companion://authCallback';

const EmailCallbackInner: React.FC = () => {
  const history = useHistory();
  const { state, dispatch } = useActivation();
  const base = useActivationBase();
  const redirectParam = buildRedirectParam(
    sessionStorage.getItem(REDIRECT_TO_SESSION_KEY) || CANOPY_NATIVE_CALLBACK,
  );

  useEffect(() => {
    const navigateToAuth = () => history.replace(`${base}/auth${redirectParam}`);

    const handleCallback = async () => {
      await new Promise((resolve) => { setTimeout(resolve, 1500); });

      const { data: { session } } = await getSupabaseClient().auth.getSession();
      if (!session) {
        logError('[SleepioEmailCallback]', 'No session after magic link');
        navigateToAuth();
        return;
      }

      dispatch({ type: 'SET_SESSION', session });

      if (state.accountIntent === AccountIntent.Create) {
        await activateSleepioConfirm({ accessToken: session.access_token });
        dispatch({ type: 'SET_STEP', step: 'coverage_form' });
        history.replace(`${base}/coverage${redirectParam}`);
      } else {
        dispatch({ type: 'SET_STEP', step: 'activation_complete' });
        history.replace(`${base}/complete${redirectParam}`);
      }
    };

    handleCallback().catch((err) => {
      logError('[SleepioEmailCallback]', 'Callback failed', err);
      navigateToAuth();
    });
  }, []); // eslint-disable-line

  return (
    <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
      <CircularProgress />
    </Box>
  );
};

export const EmailCallback: React.FC = () => (
  <ThemeProvider theme={sleepioTheme}>
    <EmailCallbackInner />
  </ThemeProvider>
);
